import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeatureService } from '../services/feature.service';
import { IframeCoordinatorClientService } from '../services/iframe-coordinator-client.service';

/**
 * Only allows navigation to a route with an externalPath set if the navigation was triggered by a browser event.
 * Otherwise, will request navigation by the host application.
 * This ensures client and host url's match and prevents race conditions between navigation and page data.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigateGuard implements CanActivate, CanActivateChild {
  constructor(
    private readonly feature: FeatureService,
    private readonly router: Router,
    private readonly ifc: IframeCoordinatorClientService
  ) {}

  public canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.canActivate(childRoute, state);
  }

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.feature.hasFeature('adminUI.v3.IFC.migration').pipe(
      map((hasIfcFeature) => {
        const hasIfc = hasIfcFeature && this.ifc.isActive;
        const isTriggeredByBrowser = this.router.getCurrentNavigation().trigger === 'popstate';
        const isRouteConfigured = !!route?.data?.externalPath;
        const path = this.constructPath(route);
        const fullExternalPath = hasIfc && path && this.ifc.client.urlFromHostPath(path);
        const hasNewPath = fullExternalPath !== window.top.location.toString();

        if (hasIfc && isRouteConfigured && hasNewPath && !isTriggeredByBrowser) {
          // Navigation was not triggered by the browser, cancel navigation and request navigation from the host application

          this.ifc.navigate(path, {
            transform: 'hostRelative'
          });

          return false;
        }

        return true;
      })
    );
  }

  private constructPath(route: ActivatedRouteSnapshot): string {
    if (!route?.data?.externalPath) {
      return null;
    }

    let output = route.data.externalPath(route.paramMap);

    if (Object.keys(route.queryParams).length > 0) {
      output = `${output}?${new URLSearchParams(route.queryParams).toString()}`;
    }

    return output;
  }
}
